import Phaser from 'phaser';
import Player from './Player';
import GameScene from '../scenes/Game';
import { NodeConfig } from './NodeConfig';
import { NodeConfigFactory } from './NodeConfigFactory';
import { NodeDisplay } from './NodeDisplay';
import { NodeDragHandler } from './NodeDragHandler';

export default class Node extends Phaser.GameObjects.Container {
    
    // Constants: Desktop mode adjustments
    private static readonly DESKTOP_X_ADJUST_FACTOR = 2.3704;
    private static readonly DESKTOP_SCALE = 1.3;

    public id: string;
    public player: Player;
    public config: NodeConfig;
    public counter: number;
    public scoreStore: number;
    public originalX: number;
    public originalY: number;
    private relatedScene: GameScene;
    private nodeDisplay: NodeDisplay;
    private nodeDragHandler: NodeDragHandler;
    private nodeCircleInteractiveRadius = 40;

    constructor(scene: GameScene, id: string, x: number, y: number, player: Player, config: any = {}) {
        super(scene, x, y);
        this.id = id;
        this.relatedScene = scene;
        this.originalX = x;
        this.originalY = y;
        this.player = player;
        this.config = NodeConfigFactory.createConfig(config);
        this.counter = this.config.initPopulation;
        this.scoreStore = this.config.scoreStore;

        scene.add.existing(this);

        // Configure Interactive
        this.setInteractive(
            new Phaser.Geom.Circle(0, 0, this.nodeCircleInteractiveRadius),
            Phaser.Geom.Circle.Contains,
            true
        );

        // Initialize helper classes
        this.nodeDisplay = new NodeDisplay(scene, this, player, this.config, this.counter, this.nodeCircleInteractiveRadius);
        this.nodeDragHandler = new NodeDragHandler(scene, this, this.originalX, this.originalY);
    }

    public static createFromMobile(scene: GameScene, id: string, x: number, y: number, player: any, config: any = {}): Node {
        return new Node(scene, id, x, y, player, config);
    }

    public static createFromDesktop(scene: GameScene, id: string, x: number, y: number, player: Player, config: NodeConfig): Node {
        // Game Mode Adjust
        x = x * Node.DESKTOP_X_ADJUST_FACTOR;
        return (new Node(scene, id, x, y, player, config)).setScale(Node.DESKTOP_SCALE);
    }

    public setVisibleText(visible: boolean) {
        this.nodeDisplay.setVisibleText(visible);
    }

    public calculateUnitsToMove(movementPercentage: number): number {
        return this.nodeDragHandler.calculateUnitsToMove(movementPercentage);
    }

    public create() {
        // Node Timer
        this.relatedScene.time.addEvent({
            delay: 1000,
            callback: this.advanceTimeAsNode,
            callbackScope: this,
            repeat: -1,
            loop: false,
            timeScale: this.config.generationSpeed * this.relatedScene.getBootloaderScene().AppData.gameSpeedFactor(),
        });

        // Composition
        this.nodeDisplay.create();
    }

    public update() {
        this.nodeDisplay.updateDisplay(this.counter, this.player.color, this.nodeDisplay.getSpriteFrame());
    }

    private advanceTimeAsNode() {
        if (
            this.player.type != Player.neutralType
            && (this.counter < this.config.maxPopulation || this.config.maxPopulation == -1)
            && (this.player.population < this.player.supply || this.player.supply == -1)
        ) {
            this.player.increasePopulation(1);
            this.counter++;
        }
        this.scoreProgress();
    }

    private scoreProgress() {
        if (this.scoreStore <= 0) {
            // Temple empty 
            if (this.config.type === NodeConfigFactory.Types.Temple) {
                this.nodeDisplay.updateTempleEmptySpriteFrame();
            }
            return;
        }
        this.scoreStore -= this.config.scoreLossFactor;
        if (this.player.type === Player.humanType) {
            this.relatedScene.increaseScore(this.config.scoreLossFactor);
        }
    }
}